import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { modulesConfig } from '@/containers/SubscriptionFormPageNew/config'

const useRetryLeadButton = ({
  actionsInProgress,
  updateLeadDispatch,
  saleDataState,
  callback,
  hasBeenRetried,
  creationDate,
  saleType,
}) => {
  const { t } = useTranslation()
  const { isAdmin, country, userId } = useSelector(selectAuthData)

  const additionalSalesConfig = modulesConfig[country]?.additionalSales
  const lockDurationMinutes =
    additionalSalesConfig?.retryLockMinutesBySaleType?.[saleType] ?? additionalSalesConfig?.retryLockMinutes ?? 7
  const confirmationRequired = (additionalSalesConfig?.retryConfirmation ?? false) && !isAdmin

  const calculateTimeRemaining = () => {
    if (!creationDate) return 0

    const retryTimestamp = new Date(creationDate).getTime()
    const now = Date.now()
    const lockDuration = lockDurationMinutes * 60 * 1000
    const unlockTime = retryTimestamp + lockDuration

    return Math.max(0, Math.floor((unlockTime - now) / 1000))
  }

  useEffect(() => {
    if (!creationDate) return

    const timer = setInterval(() => {
      updateLeadDispatch({
        canRetry: calculateTimeRemaining() <= 0,
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [creationDate])

  const isModalOpen = saleDataState?.isRetryModalOpen ?? false
  const alreadySubmitted = useRef(false)

  useEffect(() => {
    if (!isModalOpen) {
      alreadySubmitted.current = false
    }
  }, [isModalOpen])

  const timeRemaining = calculateTimeRemaining()
  const emailChecked = saleDataState?.retryEmailChecked ?? false
  const spamChecked = saleDataState?.retrySpamChecked ?? false
  const isRetrying = saleDataState?.isRetrying ?? false
  const canRetry = timeRemaining <= 0
  const hasActiveSale = !!saleDataState?.saleDatabaseId
  // The backend only lets the creator or an admin retry. A sale created in this session has no createdBy,
  // and without userId the backend decides.
  const createdBy = saleDataState?.createdBy
  const isOwnSale = !createdBy || !userId || String(createdBy) === String(userId)

  const isEnabled = hasActiveSale && (isAdmin || (isOwnSale && canRetry && !hasBeenRetried)) && !actionsInProgress
  const isConfirmEnabled = !isRetrying && (!confirmationRequired || (emailChecked && spamChecked))

  const formatTime = seconds => {
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }

  const retryText = t('additionalSales:common.retry_button')
  const showCountdown = !isAdmin && isOwnSale && !hasBeenRetried && timeRemaining > 0
  const buttonText = showCountdown ? `${retryText} (${formatTime(timeRemaining)})` : retryText

  const getHelperText = () => {
    if (isAdmin) return ''
    if (!isOwnSale) return t('additionalSales:common.retry_not_owner_message')
    if (hasBeenRetried) return t('additionalSales:common.already_retried_message')
    return ''
  }
  const helperText = getHelperText()

  const handleRetryClick = e => {
    e.preventDefault()
    updateLeadDispatch({
      isRetryModalOpen: true,
      retryEmailChecked: false,
      retrySpamChecked: false,
    })
  }

  const handleConfirm = () => {
    if (!isConfirmEnabled || alreadySubmitted.current) {
      return
    }

    alreadySubmitted.current = true
    updateLeadDispatch({
      isRetrying: true,
    })
    callback()
  }

  const handleCancel = () => {
    if (!isRetrying) {
      updateLeadDispatch({
        isRetryModalOpen: false,
      })
    }
  }

  const handleEmailChange = checked => updateLeadDispatch({ retryEmailChecked: checked })
  const handleSpamChange = checked => updateLeadDispatch({ retrySpamChecked: checked })

  return {
    t,
    retryText,
    buttonText,
    helperText,
    confirmationRequired,
    emailChecked,
    spamChecked,
    isEnabled,
    isConfirmEnabled,
    isRetrying,
    isModalOpen,
    isInlineConfirm: isModalOpen && !confirmationRequired,
    handleRetryClick,
    handleConfirm,
    handleCancel,
    handleEmailChange,
    handleSpamChange,
  }
}

export default useRetryLeadButton
